import { ProblemData } from "../../MathFactsGenerator/Constants.ts";
import { QuizQueue } from "../Quiz/quizQueue.ts";
import { expectedAnswer } from "../Quiz/answer.ts";
import { cardFaces } from "../cardFaces.ts";
import { distractorsFor, RandInt } from "./plateGame.ts";

/*
 * Math Runner (ADR 0124): three lanes, and a row of three gates comes down them
 * for every fact. The answer and two near misses are the numbers on the gates;
 * the lane the runner is in when the row arrives is the answer.
 *
 * Pure, like footballGame.ts - no timers, no DOM, no React. The component feeds
 * `advance` animation-frame deltas and `pickLane` / `moveLane` presses.
 * Randomness (the order of the numbers across the lanes, and which near misses
 * are used) comes in as `randInt`, inclusive at both ends like `getRandomInt`,
 * so tests can fix it. The near misses are Plate Smash's: `distractorsFor` asked
 * for two, shuffled in with the answer (0124 header).
 *
 * The speed follows the child (§2): a row takes `travelMs` to come down the
 * track, 5% less after a gate passed and 20% more after a stumble, between the
 * pace's floor and its starting time.
 *
 * A pick has to be made after the row appears (§1). `lane` is where the runner
 * stands and carries over from row to row; `picked` is whether the child has
 * chosen a lane since this row came out. Standing in a lane is not choosing it.
 */

export type Pace = "jog" | "run" | "sprint";

export const PACES: Pace[] = ["jog", "run", "sprint"];

/** How long a row takes to come down the track at the start of a run (§2). */
export const START_TRAVEL_MS: Record<Pace, number> = {
    jog: 9_000,
    run: 6_000,
    sprint: 4_000,
};

/** The fastest a row ever comes down, however long the child keeps passing (§2). */
export const FLOOR_TRAVEL_MS: Record<Pace, number> = {
    jog: 4_500,
    run: 3_000,
    sprint: 2_000,
};

/** A gate passed takes this fraction of the travel time; a stumble multiplies it back up (§2). */
export const PASS_FACTOR = 0.95;
export const STUMBLE_FACTOR = 1.2;

export const LANE_COUNT = 3;
/** The runner starts in the middle lane. */
export const START_LANE = 1;
export const HEARTS = 3;

export const COUNTDOWN_MS = 3000;
/** The pause after a row, so a stumble is seen before the next fact appears (§2). */
export const GAP_MS = 1000;
/** A stalled frame costs at most this much of the row (0053 §4). */
export const MAX_FRAME_MS = 250;

/**
 * How strongly the track narrows towards the horizon: a row starts 1 / this
 * far across what it will be at the runner, so 0.4. Kept gentle so the numbers
 * are readable from the moment a row appears - a young reader needs the time.
 */
export const PERSPECTIVE_DEPTH = 2.5;

export type Phase = "countdown" | "running" | "gap" | "over";

/** `late` is a row that arrived with no pick made since it appeared (§1). */
export type RowKind = "passed" | "wrong" | "late";

export interface RunnerRow {
    /** 1-based; also keys the stumble animation so each row replays it. */
    number: number;
    kind: RowKind;
    /** The problem as shown, e.g. "7 x 8". */
    front: string;
    /** The correct answer, as text. */
    answer: string;
    /** The lane the runner was in when the row arrived; null if no pick was made. */
    pickedLane: number | null;
    /** The lane that held the answer. */
    rightLane: number;
}

/** A fact that cost a heart. `picked` is the wrong number run through; null for no pick. */
export interface RunnerMiss {
    front: string;
    answer: string;
    picked: number | null;
}

export interface RunnerState {
    phase: Phase;
    pace: Pace;
    countdownLeftMs: number;
    problem: ProblemData;
    /** The number on each lane's gate, left to right. */
    numbers: number[];
    /** Time since this row appeared, capped at `travelMs`. Frozen through the gap. */
    rowMs: number;
    /** How long a row takes to come down now. */
    travelMs: number;
    gapLeftMs: number;

    /** Where the runner stands, 0 to 2. */
    lane: number;
    /** A lane has been chosen since this row appeared. */
    picked: boolean;

    hearts: number;
    /** Gates passed. */
    score: number;
    wrongGates: number;
    /** Rows that arrived with no pick made. */
    noPicks: number;
    misses: RunnerMiss[];
    /** Problems passed, as shown ("7 x 8") - for the missed-facts store. */
    correctFronts: string[];

    lastRow: RunnerRow | null;
    rows: number;
}

/** The problem as a child would read it: × and −, not the worksheet's x and -. */
export function prettyFront(front: string): string {
    return front.replace(/ x /g, " × ").replace(/ - /g, " − ");
}

export function startRun(pace: Pace, queue: QuizQueue, randInt: RandInt): RunnerState {
    const problem = queue.next();
    return {
        phase: "countdown",
        pace,
        countdownLeftMs: COUNTDOWN_MS,
        problem,
        numbers: dealLanes(problem, randInt),
        rowMs: 0,
        travelMs: START_TRAVEL_MS[pace],
        gapLeftMs: 0,
        lane: START_LANE,
        picked: false,
        hearts: HEARTS,
        score: 0,
        wrongGates: 0,
        noPicks: 0,
        misses: [],
        correctFronts: [],
        lastRow: null,
        rows: 0,
    };
}

/** The answer and two near misses, shuffled across the three lanes (§1). */
export function dealLanes(problem: ProblemData, randInt: RandInt): number[] {
    const values = [expectedAnswer(problem), ...distractorsFor(problem, randInt, LANE_COUNT - 1)];
    for (let i = values.length - 1; i > 0; i--) {
        const j = randInt(0, i);
        [values[i], values[j]] = [values[j], values[i]];
    }
    return values;
}

/** How far down the track the row is, 0 at the horizon to 1 at the runner. */
export function rowProgress(state: Pick<RunnerState, "rowMs" | "travelMs">): number {
    return Math.min(1, Math.max(0, state.rowMs / state.travelMs));
}

/**
 * How large a row is drawn, 1 at the runner and 1 / PERSPECTIVE_DEPTH at the
 * horizon. The distance is covered at a steady pace, so the picture is
 * 1 / distance: the row grows faster as it comes close.
 */
export function rowScale(progress: number): number {
    const p = Math.min(1, Math.max(0, progress));
    return 1 / (PERSPECTIVE_DEPTH - (PERSPECTIVE_DEPTH - 1) * p);
}

/** The lane (0 to 2) the runner is standing in after `lane` is chosen. Ignored outside a row. */
export function pickLane(state: RunnerState, lane: number): RunnerState {
    if (state.phase !== "running" || !Number.isInteger(lane) || lane < 0 || lane >= LANE_COUNT) {
        return state;
    }
    if (state.lane === lane && state.picked) {
        return state;
    }
    return { ...state, lane, picked: true };
}

/**
 * One lane left (-1) or right (+1). Pressing towards a wall keeps the runner
 * where it is but is still a pick: the child chose, and the lane they chose is
 * the one they are in.
 */
export function moveLane(state: RunnerState, delta: -1 | 1): RunnerState {
    return pickLane(state, Math.min(LANE_COUNT - 1, Math.max(0, state.lane + delta)));
}

/** Space or up: pick the lane the runner is already in. */
export function confirmLane(state: RunnerState): RunnerState {
    return pickLane(state, state.lane);
}

/**
 * The row has reached the runner: it passes, or stumbles on the wrong gate, or
 * stumbles because no pick was made (§1). Speed, hearts and the miss list all
 * move here.
 */
function settle(state: RunnerState): RunnerState {
    const faces = cardFaces(state.problem);
    const answer = expectedAnswer(state.problem);
    const rightLane = state.numbers.indexOf(answer);
    const kind: RowKind = !state.picked ? "late" : state.numbers[state.lane] === answer ? "passed" : "wrong";
    const passed = kind === "passed";

    const row: RunnerRow = {
        number: state.rows + 1,
        kind,
        front: faces.front,
        answer: faces.back,
        pickedLane: state.picked ? state.lane : null,
        rightLane,
    };
    const miss: RunnerMiss | null = passed
        ? null
        : { front: faces.front, answer: faces.back, picked: state.picked ? state.numbers[state.lane] : null };

    return {
        ...state,
        phase: "gap",
        gapLeftMs: GAP_MS,
        rowMs: state.travelMs,
        travelMs: passed
            ? Math.max(FLOOR_TRAVEL_MS[state.pace], state.travelMs * PASS_FACTOR)
            : Math.min(START_TRAVEL_MS[state.pace], state.travelMs * STUMBLE_FACTOR),
        hearts: passed ? state.hearts : state.hearts - 1,
        score: state.score + (passed ? 1 : 0),
        wrongGates: state.wrongGates + (kind === "wrong" ? 1 : 0),
        noPicks: state.noPicks + (kind === "late" ? 1 : 0),
        misses: miss ? [...state.misses, miss] : state.misses,
        correctFronts: passed ? [...state.correctFronts, faces.front] : state.correctFronts,
        lastRow: row,
        rows: state.rows + 1,
    };
}

/**
 * Deals the next row, or ends the run if the last heart has gone. The runner
 * keeps its lane, but the pick is cleared: the next row needs a new one (§1).
 * Draws from the queue, so the caller must not run it inside a reducer (0053 §8).
 */
function nextRow(state: RunnerState, queue: QuizQueue, randInt: RandInt): RunnerState {
    if (state.hearts <= 0) {
        return { ...state, phase: "over", gapLeftMs: 0 };
    }
    const problem = queue.next();
    return {
        ...state,
        phase: "running",
        problem,
        numbers: dealLanes(problem, randInt),
        rowMs: 0,
        gapLeftMs: 0,
        picked: false,
    };
}

/**
 * Moves time on: the countdown, the row coming down, and the pause after it.
 * The row reaching the runner settles it. Draws from the queue when the pause
 * ends, so the caller must not run it inside a reducer.
 */
export function advance(state: RunnerState, dtMs: number, queue: QuizQueue, randInt: RandInt): RunnerState {
    const dt = Math.min(Math.max(dtMs, 0), MAX_FRAME_MS);
    if (dt === 0 || state.phase === "over") {
        return state;
    }
    if (state.phase === "countdown") {
        const left = state.countdownLeftMs - dt;
        return left > 0 ? { ...state, countdownLeftMs: left } : { ...state, phase: "running", countdownLeftMs: 0 };
    }
    if (state.phase === "gap") {
        const left = state.gapLeftMs - dt;
        return left > 0 ? { ...state, gapLeftMs: left } : nextRow(state, queue, randInt);
    }

    const rowMs = Math.min(state.travelMs, state.rowMs + dt);
    const next = { ...state, rowMs };
    return rowMs >= state.travelMs ? settle(next) : next;
}
